import React, { useEffect, useState, useRef } from "react";
import axiosInstance from "../../utils/api";
import { useParams } from "react-router-dom";
import { MdAdd, MdEdit, MdDelete, MdSearch, MdDownload, MdUpload, MdClose, MdMailOutline, MdOutlineMarkEmailRead } from "react-icons/md";
import StandModal from "../../components/standModal";
import EmailSendQueue from "../../components/emailSendQueue";
import StandDetailsModal from "../../components/standDetailsModal";
import Spinner from "../../components/spinner";
import DeleteModal from "../../components/deleteModal";
import { toast } from "react-hot-toast";
import { CiCircleList, CiViewColumn } from "react-icons/ci";
import { TiExport } from "react-icons/ti";
import ExcelJS from "exceljs";
import { getSessionUser } from "../../utils/session";

const baseColumns = [
    { key: "hall", label: "Hall" },
    { key: "standNumber", label: "Stand Number" },
    { key: "exhibitorName", label: "Exhibitor Name" },
    { key: "size", label: "Size" },
    { key: "tentNumber", label: "Tent Number" },
    { key: "progress", label: "Progress" },
    { key: "email", label: "Email" },
    { key: "companyName", label: "Contractor Company" },
    { key: "chiefName", label: "Contractor Name" },
    { key: "contractorPhone", label: "Contractor Phone" },
];

const StandList = () => {
    const { id } = useParams();
    const [stands, setStands] = useState([]);
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const fileInputRef = useRef(null);

    const sessionUser = getSessionUser();
    const role = sessionUser.role;
    const isSubAdmin = role === "sub_admin";

    const [selectedIds, setSelectedIds] = useState([]);
    const [emailQueue, setEmailQueue] = useState([]);

    const [selectedStand, setSelectedStand] = useState(null);
    const [isStandModalOpen, setIsStandModalOpen] = useState(false);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [selectedHall, setSelectedHall] = useState("");
    const [selectedTent, setSelectedTent] = useState("");
    const [showColumnConfig, setShowColumnConfig] = useState(false);
    const columnConfigRef = useRef(null);
    const [visibleColumns, setVisibleColumns] = useState(() =>
        baseColumns.map((col) => col.key)
    );

    const defaultFields = [
        { key: "hall", label: "Hall" },
        { key: "standNumber", label: "Stand Number" },
        { key: "exhibitorName", label: "Exhibitor Name" },
        { key: "email", label: "Email" },
        { key: "size", label: "Size" },
        { key: "tentNumber", label: "Tent Number" },
    ];

    const defaultLabels = new Set(defaultFields.map((f) => f.label.toLowerCase()));
    const extraCustomFields = (event?.customFields || []).filter(
        (cf) => !defaultLabels.has(cf.name?.trim().toLowerCase())
    );

    const fetchEvent = async () => {
        try {
            const res = await axiosInstance.get(`/event/${id}`);
            setEvent(res.data.event || res.data.data || null);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load event data");
        }
    };

    const fetchStands = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get(`/stand/${id}`);
            setStands(res.data.stands || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEvent();
        fetchStands();
    }, [id]);

    const handleDownloadTemplate = async () => {
        if (!event) {
            toast.error("Event data not loaded");
            return;
        }
        try {
            const hallOptions = Array.from({ length: event.hallsNumber }, (_, i) => `Hall ${i + 1}`);
            const tentOptions = Array.from({ length: event.tentsNumber }, (_, i) => `Tent ${i + 1}`);

            const allFields = [
                ...defaultFields.map(f => f.label),
                ...extraCustomFields.map(cf => cf.name)
            ];

            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Stands Template');

            worksheet.addRow(allFields);

            worksheet.addRow([
                'Hall 1',
                'A-101',
                'ABC Company',
                'exhibitor@example.com',
                '50',
                'Tent 1',
                ...extraCustomFields.map(() => '')
            ]);

            worksheet.columns = allFields.map(() => ({ width: 20 }));

            for (let i = 2; i <= 1000; i++) {

                worksheet.getCell(`A${i}`).dataValidation = {
                    type: 'list',
                    allowBlank: false,
                    formulae: [`"${hallOptions.join(',')}"`],
                    showErrorMessage: true,
                    errorStyle: 'error',
                    errorTitle: 'Invalid Entry',
                    error: 'Please select a valid hall from the dropdown list',
                    showInputMessage: true,
                    promptTitle: 'Select Hall',
                    prompt: 'Choose a hall from the dropdown list'
                };

                worksheet.getCell(`E${i}`).dataValidation = {
                    type: 'list',
                    allowBlank: false,
                    formulae: [`"${tentOptions.join(',')}"`],
                    showErrorMessage: true,
                    errorStyle: 'error',
                    errorTitle: 'Invalid Entry',
                    error: 'Please select a valid tent from the dropdown list',
                    showInputMessage: true,
                    promptTitle: 'Select Tent',
                    prompt: 'Choose a tent from the dropdown list'
                };
            }

            worksheet.getRow(1).font = { bold: true };
            worksheet.getRow(1).fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFFFFFFF' }
            };

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            const fileName = `${event.name.replace(/\s+/g, '_')}_Stands_Template.xlsx`;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.click();
            window.URL.revokeObjectURL(url);

            toast.success("Template downloaded successfully");
        } catch (err) {
            console.error("Download error:", err);
            toast.error("Failed to download template");
        }
    };

    const handleExportStands = async () => {
        if (!stands.length) {
            toast.error("No stands to export");
            return;
        }

        try {
            const XLSX = await import("xlsx");

            const headers = [
                "Hall",
                "Stand Number",
                "Exhibitor Name",
                "Email",
                "Size",
                "Tent Number",
                "Progress %",

                "Stand builder Name",
                "Stand builder Phone",
                "Stand builder Email",

                "Plan Status",
                "Plan Uploaded At",

                "Arrival Date",
                "Contractor Company",
                "Contractor Chief",
                "Contractor Phone",
                "Contractor Email",

                "24H Electricity",
                "Amperage",
                "Lines Count",
                "Phase",
                "Electricity Comment",

                "Internet Needed",
                "Internet Type",
                "Speed Mbps",
                "RJ45 Count",
                "Internet Comment",

                "Last Update Date",
                "Last Update Comment",
                "Photos Count",

                ...extraCustomFields.map(cf => cf.name),
            ];

            const rows = stands.map(stand => {
                const lastUpdate = stand.dailyUpdates?.length
                    ? stand.dailyUpdates[stand.dailyUpdates.length - 1]
                    : null;

                const baseData = [
                    stand.hall || "",
                    stand.standNumber || "",
                    stand.exhibitorName || "",
                    stand.email || "",
                    stand.size || "",
                    stand.tentNumber || "",
                    stand.progress ?? 0,

                    stand.standiste?.name || "",
                    stand.standiste?.phone || "",
                    stand.standiste?.email || "",

                    stand.plan?.status || "",
                    stand.plan?.uploadedAt
                        ? new Date(stand.plan.uploadedAt).toLocaleString()
                        : "",

                    stand.onsite?.arrivalDate
                        ? new Date(stand.onsite.arrivalDate).toLocaleDateString()
                        : "",
                    stand.onsite?.contractor?.companyName || "",
                    stand.onsite?.contractor?.chiefName || "",
                    stand.onsite?.contractor?.phone || "",
                    stand.onsite?.contractor?.email || "",

                    stand.electricity?.need24h ? "Yes" : "No",
                    stand.electricity?.amperage || "",
                    stand.electricity?.linesCount || "",
                    stand.electricity?.phase || "",
                    stand.electricity?.comment || "",

                    stand.internet?.needed ? "Yes" : "No",
                    stand.internet?.type || "",
                    stand.internet?.speedMbps || "",
                    stand.internet?.rj45Count || "",
                    stand.internet?.comment || "",

                    lastUpdate?.date
                        ? new Date(lastUpdate.date).toLocaleString()
                        : "",
                    lastUpdate?.comment || "",
                    lastUpdate?.photos?.length || 0,
                ];

                const customData = extraCustomFields.map(
                    cf => stand.customFields?.[cf.name] ?? ""
                );

                return [...baseData, ...customData];
            });

            const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

            worksheet["!cols"] = headers.map(() => ({ wch: 24 }));

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Stands");

            const fileName = `${event?.name?.replace(/\s+/g, "_") || "Event"}_Full_Report.xlsx`;

            XLSX.writeFile(workbook, fileName);

            toast.success("Full stand report exported successfully");
        } catch (err) {
            console.error("Export error:", err);
            toast.error("Failed to export stand report");
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!event) {
            toast.error("Event data not loaded");
            return;
        }

        try {
            setUploading(true);

            const XLSX = await import("xlsx");

            const arrayBuffer = await file.arrayBuffer();
            const workbook = XLSX.read(arrayBuffer, { type: "array" });
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            if (!jsonData.length) {
                toast.error("No data found in the Excel file");
                return;
            }

            const standsToCreate = jsonData.map((row, index) => ({
                event: id,
                hall: row["Hall"] || "",
                standNumber: row["Stand Number"]?.toString().trim() || null,
                exhibitorName: row["Exhibitor Name"] || "",
                email: row["Email"] ? String(row["Email"]).trim().toLowerCase() : "",
                size: row["Size"] ? String(row["Size"]) : "",
                tentNumber: row["Tent Number"] || "",
                customFields: Object.fromEntries(
                    extraCustomFields
                        .map(cf => [cf.name, row[cf.name]])
                        .filter(([, value]) => value !== undefined && value !== null && value !== "")
                ),
                __row: index + 2,
            }));

            console.log("Parsed rows:", jsonData.slice(0, 3));
            console.log("Mapped stands:", standsToCreate.slice(0, 3));

            const invalidRows = standsToCreate.filter(
                (stand) => !stand.standNumber || !stand.exhibitorName
            );

            if (invalidRows.length) {
                toast.error(
                    `Missing Stand Number or Exhibitor Name in ${invalidRows.length} row(s)`
                );
                return;
            }

            const badEmails = standsToCreate.filter(
                (s) => s.email && !/^\S+@\S+\.\S+$/.test(s.email)
            );
            if (badEmails.length) {
                toast.error(`Invalid email in row(s): ${badEmails.map((s) => s.__row).join(", ")}`);
                return;
            }

            if (event.standsLimit != null && stands.length + standsToCreate.length > event.standsLimit) {
                toast.error(
                    `Stands limit is ${event.standsLimit}. You have ${stands.length} and are uploading ${standsToCreate.length}.`
                );
                return;
            }

            try {
                await axiosInstance.post("/stand", standsToCreate);

                toast.success(`${standsToCreate.length} stand(s) successfully uploaded`);
                fetchStands();
                fileInputRef.current && (fileInputRef.current.value = "");

            } catch (err) {
                console.error("Bulk upload failed:", err);
                toast.error(
                    err?.response?.data?.message || "Failed to upload stand list"
                );
            }

        } catch (err) {
            console.error("Upload error:", err);
            toast.error(
                err?.response?.data?.message || "Failed to upload Excel file"
            );
        } finally {
            setUploading(false);
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = () => {
        const ids = filteredStands.map((s) => s._id);
        const allSelected = ids.every((id) => selectedIds.includes(id));
        setSelectedIds(allSelected ? [] : ids);
    };

    const queueEmails = (standsToQueue) => {
        setEmailQueue((prev) => {
            const active = new Set(
                prev.filter((i) => i.status === "pending" || i.status === "sending").map((i) => i.id)
            );
            const additions = standsToQueue
                .filter((s) => !active.has(s._id))
                .map((s) => ({
                    id: s._id,
                    standNumber: s.standNumber,
                    exhibitorName: s.exhibitorName,
                    isResend: !!s.isEmailSent,
                    status: "pending",
                }));
            return [...prev, ...additions];
        });
    };

    const handleSendSingleEmail = (stand) => queueEmails([stand]);

    const handleSendBulkEmails = () => {
        const stands = filteredStands.filter((s) => selectedIds.includes(s._id));
        queueEmails(stands);
        setSelectedIds([]);
    };

    const handleDelete = async () => {
        try {
            await axiosInstance.delete(`/stand/${selectedStand._id}`);
            setIsDeleteOpen(false);
            toast.success("Stand deleted successfully");
            fetchStands();
        } catch (err) {
            console.error(err);
            toast.error("Failed to delete stand");
        }
    };

    const getStatusColor = (level) => {
        switch (level) {
            case "ok":
                return "bg-emerald-500";
            case "delayed":
                return "bg-amber-500";
            case "at_risk":
                return "bg-rose-600";
            default:
                return "bg-slate-300";
        }
    };

    const allColumns = [
        ...baseColumns,
        ...extraCustomFields.map((cf) => ({
            key: `custom_${cf.name}`,
            label: cf.name,
        })),
    ];

    const filteredStands = stands.filter((stand) => {
        const matchesSearch =
            stand.exhibitorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            stand.standNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            stand.hall?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesHall = selectedHall ? stand.hall === selectedHall : true;
        const matchesTent = selectedTent ? stand.tentNumber === selectedTent : true;

        return matchesSearch && matchesHall && matchesTent;
    });

    const limitReached = event?.standsLimit != null && stands.length >= event.standsLimit;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (columnConfigRef.current && !columnConfigRef.current.contains(event.target)) {
                setShowColumnConfig(false);
            }
        };

        if (showColumnConfig) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showColumnConfig]);

    if (loading) {
        return <Spinner message={"Loading stand list please wait..."} />
    }

    if (uploading) {
        return <Spinner message={"Uploading stand list please wait..."} />
    }

    const hallOptions = event
        ? Array.from({ length: event.hallsNumber }, (_, i) => `Hall ${i + 1}`)
        : [];

    const tentOptions = event
        ? Array.from({ length: event.tentsNumber }, (_, i) => `Tent ${i + 1}`)
        : [];

    return (
        <div className="space-y-4 p-6">

            <div className="flex items-center justify-between">

                <div className="mb-8">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-4">
                            <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg shadow-blue-500/30">
                                <CiCircleList className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-lg font-bold text-gray-900">
                                    Stand List
                                </h1>
                                <p className="text-gray-600 text-xs">
                                    Manage and organize all exhibition stands
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">

                    {!isSubAdmin && <button
                        onClick={handleExportStands}
                        disabled={!stands.length}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <TiExport />
                        Export Stands
                    </button>}

                    {!isSubAdmin && <button
                        onClick={handleDownloadTemplate}
                        disabled={!event}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <MdDownload />
                        Download Template
                    </button>}

                    {!isSubAdmin && <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading || !event || limitReached}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <MdUpload />
                        {uploading ? "Uploading..." : "Upload Excel"}
                    </button>}

                    {!isSubAdmin && <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileUpload}
                        className="hidden"
                    />}

                    {!isSubAdmin && <button
                        disabled={limitReached}
                        title={limitReached ? "Stands limit reached" : ""}
                        onClick={() => {
                            setSelectedStand(null);
                            setIsStandModalOpen(true);
                        }}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
                    >
                        <MdAdd />
                        Add Stand
                    </button>}
                    {!isSubAdmin && selectedIds.length > 0 && (
                        <button
                            onClick={handleSendBulkEmails}
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition text-sm"
                        >
                            <MdMailOutline />
                            Send Email ({selectedIds.length})
                        </button>
                    )}
                </div>
            </div>

            <div className="flex flex-wrap gap-3 items-center">
                <div className="relative w-64">
                    <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Search stands..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                </div>

                <select
                    value={selectedHall}
                    onChange={(e) => setSelectedHall(e.target.value)}
                    className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                    <option value="">All Halls</option>
                    {hallOptions.map((hall) => (
                        <option key={hall} value={hall}>{hall}</option>
                    ))}
                </select>

                <select
                    value={selectedTent}
                    onChange={(e) => setSelectedTent(e.target.value)}
                    className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                    <option value="">All Tents</option>
                    {tentOptions.map((tent) => (
                        <option key={tent} value={tent}>{tent}</option>
                    ))}
                </select>

                <button
                    onClick={() => {
                        setSelectedHall("");
                        setSelectedTent("");
                        setSearchTerm("");
                    }}
                    className="px-3 py-2 text-sm bg-slate-200 hover:bg-slate-300 rounded-lg transition-colors"
                >
                    Clear Filters
                </button>

                <div className="relative ml-auto" ref={columnConfigRef}>
                    <button
                        onClick={() => setShowColumnConfig((prev) => !prev)}
                        className="flex items-center gap-2 px-3 py-2 bg-slate-200 text-slate-700 border border-slate-300 rounded-lg text-sm hover:bg-slate-300 transition-colors shadow-md"
                    >
                        <CiViewColumn />
                        Configure Columns
                    </button>

                    {showColumnConfig && (
                        <div className="absolute right-0 mt-2 w-96 bg-white border border-slate-200 rounded-lg shadow-xl z-50 overflow-hidden">

                            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
                                <div>
                                    <h3 className="text-sm font-semibold text-slate-700">Configure Columns</h3>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        {visibleColumns.length} of {allColumns.length} selected
                                    </p>
                                </div>
                                <button
                                    onClick={() => setShowColumnConfig(false)}
                                    className="text-slate-400 hover:text-slate-600 transition-colors p-1 hover:bg-slate-200 rounded"
                                >
                                    <MdClose className="text-lg" />
                                </button>
                            </div>

                            <div className="max-h-72 overflow-y-auto p-3">
                                <div className="grid grid-cols-2 gap-1.5">
                                    {allColumns.map((col) => (
                                        <label
                                            key={col.key}
                                            className="flex items-center gap-2.5 px-3 py-2.5 text-sm hover:bg-blue-50 rounded-md cursor-pointer transition-all group border border-transparent hover:border-blue-200"
                                        >
                                            <div className="relative flex items-center justify-center">
                                                <input
                                                    type="checkbox"
                                                    checked={visibleColumns.includes(col.key)}
                                                    onChange={() => {
                                                        setVisibleColumns((prev) =>
                                                            prev.includes(col.key)
                                                                ? prev.filter((c) => c !== col.key)
                                                                : [...prev, col.key]
                                                        );
                                                    }}
                                                    className="peer sr-only"
                                                />
                                                <div className="w-5 h-5 border-2 border-slate-300 rounded bg-white peer-checked:bg-blue-600 peer-checked:border-blue-600 transition-all duration-200 flex items-center justify-center">
                                                    {visibleColumns.includes(col.key) && (
                                                        <svg
                                                            className="w-3 h-3 text-white"
                                                            fill="none"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth="3"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor"
                                                        >
                                                            <path d="M5 13l4 4L19 7"></path>
                                                        </svg>
                                                    )}
                                                </div>
                                            </div>
                                            <span className="text-slate-700 group-hover:text-slate-900 font-medium select-none">
                                                {col.label}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="border-t border-slate-200 px-4 py-3 bg-slate-50 flex gap-2">
                                <button
                                    onClick={() => setVisibleColumns(allColumns.map(c => c.key))}
                                    className="flex-1 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                >
                                    Select All
                                </button>
                                <button
                                    onClick={() => setVisibleColumns([])}
                                    className="flex-1 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded transition-colors"
                                >
                                    Clear All
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                            <tr>
                                {!isSubAdmin && (
                                    <th className="px-4 py-2 text-left w-8">
                                        <input
                                            type="checkbox"
                                            checked={filteredStands.length > 0 && filteredStands.every((s) => selectedIds.includes(s._id))}
                                            onChange={toggleSelectAll}
                                        />
                                    </th>
                                )}
                                {allColumns
                                    .filter((col) => visibleColumns.includes(col.key))
                                    .map((col) => (
                                        <th key={col.key} className="px-4 py-2 text-left font-medium text-slate-600">
                                            {col.label}
                                        </th>
                                    ))}
                                {!isSubAdmin && <th className="px-4 py-2 text-right font-medium text-slate-600">Actions</th>}
                            </tr>
                        </thead>

                        <tbody className="divide-y">
                            {filteredStands.map((stand) => (
                                <tr key={stand._id} className="hover:bg-slate-50">

                                    {!isSubAdmin && (
                                        <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.includes(stand._id)}
                                                onChange={() => toggleSelect(stand._id)}
                                            />
                                        </td>
                                    )}
                                    {allColumns
                                        .filter((col) => visibleColumns.includes(col.key))
                                        .map((col) => {

                                            const openDetails = () => {
                                                setSelectedStand(stand);
                                                setIsDetailsOpen(true);
                                            };

                                            if (col.key === "hall") {
                                                return (
                                                    <td key={col.key} onClick={openDetails} className="px-4 py-2 font-medium text-slate-900 cursor-pointer">
                                                        <div className="flex items-center gap-2">
                                                            <span
                                                                className={`w-2.5 h-2.5 rounded-full ${getStatusColor(stand.scheduleStatus?.level)}`}
                                                                title={stand.scheduleStatus?.message}
                                                            />
                                                            {stand.hall || "-"}
                                                        </div>
                                                    </td>
                                                );
                                            }

                                            if (col.key === "progress") {
                                                return (
                                                    <td key={col.key} onClick={openDetails} className="px-4 py-2 cursor-pointer">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                                                <div
                                                                    className="h-full bg-blue-600"
                                                                    style={{ width: `${stand.progress || 0}%` }}
                                                                />
                                                            </div>
                                                            <span className="text-xs font-medium text-slate-600">
                                                                {(stand.progress || 0).toFixed(2)}%
                                                            </span>
                                                        </div>
                                                    </td>
                                                );
                                            }

                                            if (col.key === "companyName") {
                                                return (
                                                    <td key={col.key} onClick={openDetails} className="px-4 py-2 text-slate-700 cursor-pointer">
                                                        {stand.onsite?.contractor?.companyName || "-"}
                                                    </td>
                                                );
                                            }

                                            if (col.key === "chiefName") {
                                                return (
                                                    <td key={col.key} onClick={openDetails} className="px-4 py-2 text-slate-700 cursor-pointer">
                                                        {stand.onsite?.contractor?.chiefName || "-"}
                                                    </td>
                                                );
                                            }

                                            if (col.key === "contractorPhone") {
                                                return (
                                                    <td key={col.key} onClick={openDetails} className="px-4 py-2 text-slate-700 cursor-pointer">
                                                        {stand.onsite?.contractor?.phone || "-"}
                                                    </td>
                                                );
                                            }

                                            if (col.key.startsWith("custom_")) {
                                                const fieldName = col.key.replace("custom_", "");
                                                return (
                                                    <td key={col.key} onClick={openDetails} className="px-4 py-2 text-slate-700 cursor-pointer">
                                                        {stand.customFields?.[fieldName] || "-"}
                                                    </td>
                                                );
                                            }

                                            return (
                                                <td key={col.key} onClick={openDetails} className="px-4 py-2 text-slate-700 cursor-pointer">
                                                    {stand[col.key] || "-"}
                                                </td>
                                            );
                                        })}

                                    {!isSubAdmin && (
                                        <td className="px-4 py-2 text-right space-x-1 whitespace-nowrap">
                                            <button
                                                onClick={() => stand.email && handleSendSingleEmail(stand)}
                                                disabled={!stand.email}
                                                className={`p-1.5 hover:bg-slate-100 rounded-md disabled:opacity-30 disabled:cursor-not-allowed ${stand.isEmailSent ? "text-emerald-600" : "text-amber-600"
                                                    }`}
                                                title={
                                                    !stand.email
                                                        ? "No email set for this stand"
                                                        : stand.isEmailSent
                                                            ? "Resend access email"
                                                            : "Send access email"
                                                }
                                            >
                                                {stand.isEmailSent ? <MdOutlineMarkEmailRead /> : <MdMailOutline />}
                                            </button>

                                            <button onClick={() => { setSelectedStand(stand); setIsStandModalOpen(true); }} className="p-1.5 hover:bg-slate-100 rounded-md text-emerald-600">
                                                <MdEdit />
                                            </button>
                                            <button onClick={() => { setSelectedStand(stand); setIsDeleteOpen(true); }} className="p-1.5 hover:bg-slate-100 rounded-md text-rose-600">
                                                <MdDelete />
                                            </button>
                                        </td>
                                    )}

                                </tr>
                            ))}

                            {!loading && filteredStands.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={visibleColumns.length + 1 + (!isSubAdmin ? 1 : 0)}
                                        className="px-4 py-8 text-center text-slate-500"
                                    >
                                        No stands found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <StandModal
                isOpen={isStandModalOpen}
                onClose={() => setIsStandModalOpen(false)}
                stand={selectedStand}
                eventId={id}
                onSuccess={fetchStands}
            />

            <StandDetailsModal
                isOpen={isDetailsOpen}
                onClose={() => setIsDetailsOpen(false)}
                stand={selectedStand}
                isSubAdmin={isSubAdmin}
            />

            <DeleteModal
                isOpen={isDeleteOpen}
                onClose={() => setIsDeleteOpen(false)}
                onConfirm={handleDelete}
                title="Delete Stand"
                message="Are you sure you want to delete this stand?"
            />
            <EmailSendQueue queue={emailQueue} setQueue={setEmailQueue} />
        </div >
    );
};

export default StandList;